import paymentMethods from '@/assets/json/paymentMethods.json';

export const usePayments = () => {
  const { payments: paymentsRuntime } = useRuntimeConfig().public;
  const { selectWithdrawMethod, selectDepositMethod } = useWalletStore();

  const {
    isLoading,
    payments: paymentsFromApi,
    fallback,
  } = storeToRefs(usePaymentsStore());

  const { userCurrency, userId } = useUserData();

  const userHasFallbackDepositEnable = computed<boolean>(() => {
    return !!(
      fallback.value.fallback_enabled &&
      (fallback.value.fallback_all_users || fallback.value.whitelist_users)
    );
  });

  const isCurrentUserTester = computed(() => {
    if (paymentsRuntime.testUsers) {
      const usersInWhiteList = paymentsRuntime.testUsers.split(',');

      return !!usersInWhiteList.includes(String(userId.value));
    }

    return false;
  });

  async function fetchPaymentsFromApi() {
    if (!paymentsRuntime.getFromApi) {
      return;
    }

    try {
      const { ApiService } = useApiService();

      await ApiService.get('/payment-providers')
        .then(({ data }: any) => {
          const paymentsData = data as PaymentsFromApi;

          paymentsFromApi.value.withdraw = paymentsData.withdraw;
          paymentsFromApi.value.deposit = paymentsData.deposit;
          if (paymentsData.fallback && paymentsData.fallback.fallback_enabled) {
            fallback.value.fallback_enabled =
              paymentsData.fallback.fallback_enabled;
          } else {
            fallback.value = {
              fallback_enabled: false,
              whitelist_users: false,
              fallback_all_users: false,
            };
          }
        })
        .catch((error: any) => {
          console.error('fetchPaymentsFromApi error: ', error);
        });
    } catch (err) {
      console.error('fetchPaymentsFromApi err: ', err);
    }
  }

  async function fetchBankList() {
    isLoading.value = true;

    try {
      const { ApiService } = useApiService();

      return await ApiService.get('/bff/users/bank-list');
    } catch (err) {
      console.error('fetchBankList err: ', err);
    } finally {
      isLoading.value = false;
    }
  }

  const allDepositMethods = computed(() => {
    const depositMethods = ref<PaymentsItem[]>([]);

    if (
      paymentsFromApi.value?.deposit &&
      paymentsFromApi.value?.deposit.length > 0
    ) {
      depositMethods.value = paymentsFromApi.value?.deposit;
      // depositMethods.value = paymentMethods.deposit as PaymentsItem[];
    } else {
      depositMethods.value = paymentMethods.deposit as PaymentsItem[];
    }

    depositMethods.value = depositMethods.value
      .filter((method) => {
        if (method.is_test) return isCurrentUserTester.value;

        return !!method.is_active;
      })
      .filter((method) => {
        if (method.is_test) return isCurrentUserTester.value;

        const isAllowedUserCurrency = computed(() => {
          return method.currencies.includes(String(userCurrency.value));
        });

        return isAllowedUserCurrency.value;
      }) as PaymentsItem[];

    return depositMethods.value;
  });

  const allWithdrawMethods = computed(() => {
    const withdrawMethods = ref<PaymentsItem[]>([]);

    if (
      paymentsFromApi.value?.withdraw &&
      paymentsFromApi.value?.withdraw.length > 0
    ) {
      withdrawMethods.value = paymentsFromApi.value?.withdraw;
      // withdrawMethods.value = paymentMethods.withdraw as PaymentsItem[];
    } else {
      withdrawMethods.value = paymentMethods.withdraw as PaymentsItem[];
    }

    return withdrawMethods.value
      .filter((method) => {
        if (method.is_test) return isCurrentUserTester.value;

        return !!method.is_active;
      })
      .filter((method) => {
        if (method.is_test) return isCurrentUserTester.value;

        const isAllowedUserCurrency = computed(() => {
          return method.currencies.includes(String(userCurrency.value));
        });

        return isAllowedUserCurrency.value;
      }) as PaymentsItem[];
  });

  const numberOfDepositMethods = computed<number>(() => {
    return allDepositMethods.value.length;
  });

  const defaultSelectedDepositMethod = computed<PaymentsMethodsType>(() => {
    if (!allDepositMethods.value[0]) {
      return 'pix';
    }

    return allDepositMethods.value[0].slug as PaymentsMethodsType;
  });

  const numberOfWithdrawMethods = computed<number>(() => {
    return allWithdrawMethods.value.length;
  });

  const defaultSelectedWithdrawMethod = computed<PaymentsMethodsType>(() => {
    if (!allWithdrawMethods.value[0]) {
      return 'pix';
    }

    return allWithdrawMethods.value[0].slug as PaymentsMethodsType;
  });

  async function fetchPaymentsFromApiNew() {
    isLoading.value = true;
    try {
      await fetchPaymentsFromApi();
    } catch (err) {
      isLoading.value = false;

      return;
    }
    if (!fallback.value.fallback_enabled) {
      isLoading.value = false;

      return;
    }

    try {
      const { ApiService } = useApiService();

      await ApiService.get('/payments')
        .then(({ data }: any) => {
          const paymentsData = data as PaymentsFromApi;

          paymentsFromApi.value.withdraw = paymentsData.withdraw;
          paymentsFromApi.value.deposit = paymentsData.deposit;
          if (paymentsData.fallback) {
            fallback.value.fallback_all_users =
              paymentsData.fallback.fallback_all_users;
            fallback.value.whitelist_users =
              paymentsData.fallback.whitelist_users;
            fallback.value.fallback_enabled =
              paymentsData.fallback.fallback_enabled;
          } else {
            fallback.value = {
              fallback_enabled: false,
              whitelist_users: false,
              fallback_all_users: false,
            };
          }
          sleep(300);
          selectWithdrawMethod(defaultSelectedWithdrawMethod.value);
          selectDepositMethod(defaultSelectedDepositMethod.value);
        })
        .catch((error: any) => {
          console.error('fetchPaymentsFromApi error: ', error);
        })
        .finally(() => {
          isLoading.value = false;
        });
    } catch (err) {
      console.error('fetchPaymentsFromApi err: ', err);
      isLoading.value = false;
    }
  }

  function openDepositModal() {
    const { isAuthenticated } = storeToRefs(useAuthStore());

    if (!isAuthenticated.value) {
      return;
    }

    const { userCanOnlyWithdraw } = useUserLimits();

    if (userCanOnlyWithdraw.value) {
      return;
    }

    fetchPaymentsFromApiNew();

    const { openModal } = useLayoutStore();

    openModal('deposit');
  }

  function openWithdrawModal() {
    const { isAuthenticated } = storeToRefs(useAuthStore());

    if (!isAuthenticated.value) {
      return;
    }
    fetchPaymentsFromApiNew();

    const { openModal } = useLayoutStore();

    openModal('withdraw');
  }

  return {
    fetchPaymentsFromApi,
    fetchBankList,
    paymentsFromApi,
    allDepositMethods,
    allWithdrawMethods,
    numberOfDepositMethods,
    defaultSelectedDepositMethod,
    numberOfWithdrawMethods,
    defaultSelectedWithdrawMethod,
    openDepositModal,
    openWithdrawModal,
    fetchPaymentsFromApiNew,
    userHasFallbackDepositEnable,
  };
};
